import { inject, Injectable } from '@angular/core';
import {
  IResourceTablePresetAdapter,
  ResourceTablePreset,
  ResourceTablePresetData,
  tablePresetHash,
} from '@kinet-open-network/client/shared/ui';

import {
  apiFilter,
  ApiQuery,
} from '@kinet-open-network/client/core/api-client';
import { firstValueFrom } from 'rxjs';
import { UserOption } from '../models/user-option.model';
import { AuthOptionsService } from './auth-options.service';

@Injectable({
  providedIn: 'root',
})
export class AuthResourceTablePresetAdapter
  implements IResourceTablePresetAdapter
{
  readonly #optionsService = inject(AuthOptionsService);

  async getPresets(key: string): Promise<ResourceTablePreset[]> {
    const filters = apiFilter<UserOption>()
      .category('table_presets')
      .subcategory(key);

    const apiQuery: ApiQuery = {
      perPage: -1,
      filters,
    };

    const response = await firstValueFrom(this.#optionsService.query(apiQuery));

    return Promise.all(
      response.data.map(async (option) => this.optionToPreset(option)),
    );
  }

  async savePreset(
    key: string,
    preset: Partial<ResourceTablePreset>,
  ): Promise<ResourceTablePreset> {
    if (!preset.data) {
      throw new Error('Preset data is required');
    }

    const data: Partial<UserOption> = {
      data: preset.data,
      category: 'table_presets',
      subcategory: key,
      name: preset.name || 'N/A',
      order: preset.order,
    };

    if (!preset.id) {
      const option = await firstValueFrom(this.#optionsService.create(data));

      return this.optionToPreset(option);
    } else {
      const option = await firstValueFrom(
        this.#optionsService.update(preset.id, data),
      );

      return this.optionToPreset(option);
    }
  }

  async deletePreset(key: string, preset: ResourceTablePreset): Promise<void> {
    await firstValueFrom(this.#optionsService.delete(preset.id));
  }

  private async optionToPreset(option: UserOption) {
    const hash = await tablePresetHash(option.data);

    const preset: ResourceTablePreset = {
      id: option.id,
      name: option.name ?? 'N/A',
      hash: hash,
      data: option.data as ResourceTablePresetData,
      order: Number(option.order ?? 0),
    };

    return preset;
  }
}
